// src/SettingsPanel/SettingsPanel.tsx
import { useMemo as useMemo2 } from "react";
import { useReloadGuard } from "@hopdrive/sdk-app-updater/react";

// src/shared/select-rows.ts
function selectRows(settings, keys) {
  if (!keys) return settings;
  const byKey = new Map(settings.map((setting) => [setting.key, setting]));
  return keys.map((key) => byKey.get(key)).filter((setting) => setting !== void 0);
}

// src/shared/SettingsRows.tsx
import { Box as Box2, Stack as Stack2, Typography as Typography3 } from "@mui/material";

// src/DefinitionEditor/DefinitionEditor.tsx
import { Box, Button, Stack } from "@mui/material";
import { allDefinitions } from "@hopdrive/sdk-configs/definitions";
import { HostServicesProvider, RendererRouter } from "@hopdrive/sdk-configs/renderers";

// src/DefinitionEditor/DefinitionHeader.tsx
import { Alert, Typography } from "@mui/material";
import { jsx, jsxs } from "react/jsx-runtime";
function DefinitionHeader({ setting }) {
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(Typography, { fontSize: 15, fontWeight: 600, sx: { color: "text.primary" }, children: setting.label }),
    setting.description ? /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { marginTop: "4px" }, children: setting.description }) : null,
    setting.warning ? /* @__PURE__ */ jsx(Alert, { severity: "warning", sx: { marginTop: "8px" }, children: /* @__PURE__ */ jsx(Typography, { variant: "body2", children: setting.warning }) }) : null
  ] });
}

// src/DefinitionEditor/SettingValueDisplay.tsx
import { Typography as Typography2 } from "@mui/material";
import { jsx as jsx2 } from "react/jsx-runtime";
function formatValue(value) {
  if (value === null || value === void 0) return "Not set";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value.length > 0 ? value : "Not set";
  if (typeof value === "number" || typeof value === "bigint") return String(value);
  return JSON.stringify(value, null, 2);
}
function SettingValueDisplay({ value }) {
  const text = formatValue(value);
  const isStructured = text.includes("\n");
  return /* @__PURE__ */ jsx2(
    Typography2,
    {
      "data-testid": "setting-value-display",
      component: isStructured ? "pre" : "div",
      variant: "body2",
      sx: {
        color: "text.primary",
        fontFamily: isStructured ? "monospace" : void 0,
        whiteSpace: isStructured ? "pre-wrap" : void 0,
        margin: 0
      },
      children: text
    }
  );
}

// src/DefinitionEditor/DefinitionEditor.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function DefinitionEditor({
  setting,
  pendingValue,
  hasPendingChange: hasPendingChange2 = false,
  onChange,
  onSave,
  onClear,
  readOnlyPresentation = "value",
  hostServices,
  enumOptions,
  enumLoading,
  saving = false,
  definition,
  showHeader = true,
  showActions = true
}) {
  if (setting.access !== "editable" && setting.access !== "readonly") return null;
  const isEditable = setting.access === "editable";
  const value = hasPendingChange2 ? pendingValue : setting.effectiveValue;
  const resolvedDefinition = definition ?? allDefinitions[setting.key];
  const showsControl = isEditable || readOnlyPresentation === "disabled-editor";
  const control = showsControl ? /* @__PURE__ */ jsx3(
    RendererRouter,
    {
      settingKey: setting.key,
      value,
      onChange: (next) => onChange?.(setting.key, next),
      disabled: !isEditable,
      definition: resolvedDefinition,
      enumOptions,
      enumLoading
    }
  ) : /* @__PURE__ */ jsx3(SettingValueDisplay, { value });
  const canSave = isEditable && hasPendingChange2 && Boolean(onSave);
  const canClear = isEditable && setting.currentValue !== null && Boolean(onClear);
  const actions = showActions && (canSave || canClear) ? /* @__PURE__ */ jsxs2(Stack, { direction: "row", spacing: 1, sx: { marginTop: "12px" }, children: [
    canSave ? /* @__PURE__ */ jsx3(Button, { size: "small", variant: "contained", disabled: saving, onClick: () => onSave?.(setting.key), children: "Save" }) : null,
    canClear ? /* @__PURE__ */ jsx3(Button, { size: "small", variant: "text", disabled: saving, onClick: () => onClear?.(setting.key), children: "Clear override" }) : null
  ] }) : null;
  const body = /* @__PURE__ */ jsxs2(Box, { id: `setting-${setting.key}`, sx: { opacity: isEditable ? 1 : 0.7 }, children: [
    showHeader ? /* @__PURE__ */ jsx3(DefinitionHeader, { setting }) : null,
    /* @__PURE__ */ jsx3(Box, { sx: { marginTop: showHeader ? "12px" : 0 }, children: control }),
    actions
  ] });
  return hostServices ? /* @__PURE__ */ jsx3(HostServicesProvider, { value: hostServices, children: body }) : body;
}

// src/shared/SettingsRows.tsx
import { Fragment, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function groupRows(rows) {
  const groups = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const existing = groups.get(row.category);
    if (existing) existing.push(row);
    else groups.set(row.category, [row]);
  }
  return [...groups.entries()];
}
function SettingsRows({
  rows,
  pending,
  changedKeys,
  onChange,
  onSave,
  onClear,
  readOnlyPresentation,
  hostServices,
  enumOptionsByKey,
  savingKeys,
  definitionsByKey,
  groupByCategory,
  emptyState = null
}) {
  if (rows.length === 0) return /* @__PURE__ */ jsx4(Fragment, { children: emptyState });
  const saving = new Set(savingKeys ?? []);
  const renderRow = (setting) => /* @__PURE__ */ jsx4(
    DefinitionEditor,
    {
      setting,
      pendingValue: pending[setting.key],
      hasPendingChange: changedKeys.has(setting.key),
      onChange,
      onSave,
      onClear,
      readOnlyPresentation,
      enumOptions: enumOptionsByKey?.[setting.key],
      saving: saving.has(setting.key),
      ...hostServices ? { hostServices } : {},
      ...definitionsByKey?.[setting.key] !== void 0 ? { definition: definitionsByKey[setting.key] } : {}
    },
    setting.key
  );
  if (!groupByCategory) {
    return /* @__PURE__ */ jsx4(Stack2, { spacing: 4, children: rows.map(renderRow) });
  }
  return /* @__PURE__ */ jsx4(Stack2, { spacing: 4, children: groupRows(rows).map(([category, categoryRows]) => (
    // `section-${category}` is admin's own anchor convention, kept so the Workbench's table
    // of contents can scroll to a heading the way admin's does. It is inert in the panel.
    /* @__PURE__ */ jsxs3(Box2, { id: `section-${category}`, sx: { scrollMarginTop: "16px" }, children: [
      /* @__PURE__ */ jsx4(Typography3, { variant: "overline", color: "text.secondary", children: category }),
      /* @__PURE__ */ jsx4(Stack2, { spacing: 4, sx: { marginTop: "8px" }, children: categoryRows.map(renderRow) })
    ] }, category)
  )) });
}

// src/shared/usePendingChanges.ts
import { useCallback, useMemo, useState } from "react";

// src/SettingsPanel/pending-changes.ts
function hasOwn(target, key) {
  return Object.prototype.hasOwnProperty.call(target, key);
}
function deepEqual(a, b) {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  const left = a;
  const right = b;
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  return leftKeys.every((key) => hasOwn(right, key) && deepEqual(left[key], right[key]));
}
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasPendingChange(pending, key, effectiveValue) {
  if (!hasOwn(pending, key)) return false;
  return !deepEqual(pending[key], effectiveValue);
}
function settingChanges(effectiveValue, pendingValue) {
  if (!isPlainObject(pendingValue) || !isPlainObject(effectiveValue)) return pendingValue;
  const changes = {};
  for (const [key, value] of Object.entries(pendingValue)) {
    if (!deepEqual(value, effectiveValue[key])) changes[key] = value;
  }
  return changes;
}

// src/shared/usePendingChanges.ts
function usePendingChanges(rows, options) {
  const { pendingChanges, onChange, onSave, onClear } = options;
  const [ownPending, setOwnPending] = useState({});
  const isControlled = pendingChanges !== void 0;
  const pending = pendingChanges ?? ownPending;
  const changedKeys = useMemo(() => {
    const changed = /* @__PURE__ */ new Set();
    for (const row of rows) if (hasPendingChange(pending, row.key, row.effectiveValue)) changed.add(row.key);
    return changed;
  }, [pending, rows]);
  const handleChange = useCallback(
    (key, value) => {
      if (!isControlled) setOwnPending((current) => ({ ...current, [key]: value }));
      onChange?.(key, value);
    },
    [isControlled, onChange]
  );
  const handleSave = useCallback(
    (key) => {
      const row = rows.find((setting) => setting.key === key);
      if (!row) return;
      onSave?.(key, settingChanges(row.effectiveValue, pending[key]));
    },
    [onSave, pending, rows]
  );
  const handleClear = useCallback(
    (key) => {
      if (!isControlled) {
        setOwnPending((current) => {
          if (!Object.prototype.hasOwnProperty.call(current, key)) return current;
          const { [key]: _dropped, ...rest } = current;
          return rest;
        });
      }
      onClear?.(key);
    },
    [isControlled, onClear]
  );
  return {
    pending,
    changedKeys,
    handleChange,
    handleSave: onSave ? handleSave : void 0,
    handleClear: onClear ? handleClear : void 0
  };
}

// src/SettingsPanel/SettingsPanel.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function SettingsPanel({
  settings,
  keys,
  pendingChanges,
  onChange,
  onSave,
  onClear,
  hostServices,
  enumOptionsByKey,
  savingKeys,
  groupByCategory = false,
  readOnlyPresentation = "value",
  emptyState = null
}) {
  const rows = useMemo2(() => selectRows(settings, keys), [settings, keys]);
  const { pending, changedKeys, handleChange, handleSave, handleClear } = usePendingChanges(rows, {
    pendingChanges,
    onChange,
    onSave,
    onClear
  });
  const holdsUnsavedWork = changedKeys.size > 0 || (savingKeys?.length ?? 0) > 0;
  useReloadGuard(holdsUnsavedWork, "Settings");
  return /* @__PURE__ */ jsx5(
    SettingsRows,
    {
      rows,
      pending,
      changedKeys,
      onChange: handleChange,
      onSave: handleSave,
      onClear: handleClear,
      readOnlyPresentation,
      hostServices,
      enumOptionsByKey,
      savingKeys,
      groupByCategory,
      emptyState
    }
  );
}

// src/SettingsWorkbench/SettingsWorkbench.tsx
import { useCallback as useCallback2, useMemo as useMemo4, useReducer } from "react";
import { Box as Box7 } from "@mui/material";

// src/SettingsWorkbench/SettingsJsonView.tsx
import { Box as Box3, Tab, Tabs } from "@mui/material";
import { useMemo as useMemo3 } from "react";
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function SettingsJsonView({ settings, inheritanceChain, tab, onTabChange }) {
  const tabs = useMemo3(() => {
    const overrides = {};
    const effective = {};
    for (const setting of settings) {
      if (setting.currentValue !== null) overrides[setting.key] = setting.currentValue;
      effective[setting.key] = setting.effectiveValue;
    }
    const parents = (inheritanceChain ?? []).slice(0, -1).filter((entry) => entry.config !== void 0).map((entry) => ({ label: entry.entityName, data: entry.config }));
    return [
      { label: "Overrides at this level", data: overrides },
      { label: "Effective (inherited + overrides)", data: effective },
      ...parents
    ];
  }, [settings, inheritanceChain]);
  const active = tabs[Math.min(Math.max(tab, 0), tabs.length - 1)];
  return /* @__PURE__ */ jsxs4(Box3, { "data-testid": "settings-json-view", sx: { width: "100%" }, children: [
    /* @__PURE__ */ jsx6(
      Tabs,
      {
        value: Math.min(Math.max(tab, 0), tabs.length - 1),
        onChange: (_event, next) => onTabChange(next),
        variant: "scrollable",
        scrollButtons: "auto",
        sx: { minHeight: 40, borderBottom: 1, borderColor: "divider" },
        children: tabs.map((entry) => /* @__PURE__ */ jsx6(Tab, { label: entry.label, sx: { minHeight: 40, textTransform: "none" } }, entry.label))
      }
    ),
    /* @__PURE__ */ jsx6(
      Box3,
      {
        component: "pre",
        "data-testid": "settings-json-body",
        sx: {
          margin: 0,
          marginTop: "12px",
          padding: "12px",
          borderRadius: "4px",
          border: 1,
          borderColor: "divider",
          backgroundColor: "action.hover",
          fontFamily: "monospace",
          fontSize: 12,
          overflow: "auto"
        },
        children: JSON.stringify(active.data, null, 2)
      }
    )
  ] });
}

// src/SettingsWorkbench/SettingsWorkbenchHeader.tsx
import { Box as Box4, Typography as Typography4 } from "@mui/material";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function SettingsWorkbenchHeader({
  chain,
  showBreadcrumb,
  onSelectEntity,
  headerActions
}) {
  const crumbs = showBreadcrumb ? chain ?? [] : [];
  return /* @__PURE__ */ jsxs5(
    Box4,
    {
      sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: "8px"
      },
      children: [
        crumbs.length > 0 ? /* @__PURE__ */ jsx7(
          Box4,
          {
            component: "nav",
            "data-testid": "settings-breadcrumb",
            "aria-label": "Configuration inheritance",
            sx: { display: "flex", alignItems: "center", gap: "2px", flexWrap: "wrap" },
            children: crumbs.map((entry, index) => {
              const isLast = index === crumbs.length - 1;
              return /* @__PURE__ */ jsxs5(Box4, { sx: { display: "flex", alignItems: "center" }, children: [
                index > 0 ? /* @__PURE__ */ jsx7(Typography4, { component: "span", "aria-hidden": true, sx: { color: "divider", fontSize: 12, padding: "0 2px" }, children: "\u203A" }) : null,
                /* @__PURE__ */ jsx7(
                  Box4,
                  {
                    component: "button",
                    type: "button",
                    "aria-current": isLast ? "page" : void 0,
                    onClick: () => onSelectEntity?.(entry),
                    sx: {
                      border: 0,
                      cursor: "pointer",
                      borderRadius: "12px",
                      padding: "2px 8px",
                      fontSize: 12,
                      fontWeight: isLast ? 500 : 400,
                      color: isLast ? "primary.main" : "text.secondary",
                      backgroundColor: isLast ? "action.selected" : "transparent"
                    },
                    children: entry.entityName
                  }
                )
              ] }, `${entry.level}-${entry.entityId ?? "root"}`);
            })
          }
        ) : /* @__PURE__ */ jsx7(Box4, {}),
        headerActions ? /* @__PURE__ */ jsx7(Box4, { sx: { display: "flex", alignItems: "center", gap: "8px" }, children: headerActions }) : null
      ]
    }
  );
}

// src/SettingsWorkbench/SettingsWorkbenchToc.tsx
import { Box as Box5, Typography as Typography5 } from "@mui/material";
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
function SettingsWorkbenchToc({
  sections,
  collapsedCategories,
  selectedKey,
  onToggleCategory,
  onSelectCategory,
  onSelectSetting,
  width
}) {
  return /* @__PURE__ */ jsxs6(
    Box5,
    {
      component: "nav",
      "data-testid": "settings-toc",
      "aria-label": "Settings table of contents",
      sx: {
        width,
        flexShrink: 0,
        overflowY: "auto",
        overflowX: "clip",
        borderRight: 1,
        borderColor: "divider",
        padding: "12px 8px 12px 0"
      },
      children: [
        sections.length === 0 ? /* @__PURE__ */ jsx8(Typography5, { variant: "caption", color: "text.secondary", sx: { paddingLeft: "8px" }, children: "Nothing matches." }) : null,
        sections.map((section) => {
          const collapsed = collapsedCategories.includes(section.category);
          return /* @__PURE__ */ jsxs6(Box5, { sx: { marginBottom: "16px" }, children: [
            /* @__PURE__ */ jsxs6(Box5, { sx: { display: "flex", alignItems: "center", gap: "4px" }, children: [
              /* @__PURE__ */ jsx8(
                Box5,
                {
                  component: "button",
                  type: "button",
                  "data-testid": `toc-collapse-${section.category}`,
                  "aria-expanded": !collapsed,
                  "aria-label": `${collapsed ? "Expand" : "Collapse"} ${section.category}`,
                  onClick: () => onToggleCategory(section.category),
                  sx: {
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    padding: "0 4px",
                    color: "text.secondary",
                    fontSize: 10,
                    lineHeight: 1
                  },
                  children: collapsed ? "\u25B8" : "\u25BE"
                }
              ),
              /* @__PURE__ */ jsx8(
                Box5,
                {
                  component: "button",
                  type: "button",
                  "data-testid": `toc-category-${section.category}`,
                  onClick: () => onSelectCategory(section.category),
                  sx: {
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    padding: 0,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                    fontWeight: 500,
                    // Admin dims a category whose settings are all inapplicable here rather than
                    // hiding it, so the user can still see the category exists.
                    color: section.allReadOnly ? "text.disabled" : "text.primary"
                  },
                  children: section.category
                }
              )
            ] }),
            collapsed ? null : section.settings.map((setting) => /* @__PURE__ */ jsx8(
              Box5,
              {
                component: "button",
                type: "button",
                "data-testid": `toc-item-${setting.key}`,
                "data-toc-key": setting.key,
                "aria-current": selectedKey === setting.key ? "true" : void 0,
                onClick: () => onSelectSetting(setting.key, setting.category),
                sx: {
                  display: "block",
                  border: 0,
                  background: "none",
                  cursor: "pointer",
                  padding: "2px 0",
                  marginLeft: "20px",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  color: setting.access === "readonly" || setting.deprecated ? "text.disabled" : "text.secondary",
                  textDecoration: setting.deprecated ? "line-through" : "none",
                  fontWeight: selectedKey === setting.key ? 600 : 400
                },
                children: setting.label
              },
              setting.key
            ))
          ] }, section.category);
        })
      ]
    }
  );
}

// src/SettingsWorkbench/SettingsWorkbenchToolbar.tsx
import { Box as Box6, FormControlLabel, Switch, TextField, Typography as Typography6 } from "@mui/material";
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
function SettingsWorkbenchToolbar({
  showSearch,
  showJsonToggle,
  searchTerm,
  onSearchChange,
  hideReadOnly,
  onHideReadOnlyChange,
  jsonOpen,
  onJsonOpenChange,
  shownCount,
  totalCount
}) {
  return /* @__PURE__ */ jsxs7(
    Box6,
    {
      sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        flexWrap: "wrap",
        paddingBottom: "8px",
        marginBottom: "8px",
        borderBottom: 1,
        borderColor: "divider"
      },
      children: [
        showSearch ? /* @__PURE__ */ jsxs7(Box6, { "data-testid": "settings-search", sx: { display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }, children: [
          /* @__PURE__ */ jsx9(
            TextField,
            {
              size: "small",
              value: searchTerm,
              onChange: (event) => onSearchChange(event.target.value),
              placeholder: "Filter settings...",
              inputProps: { "aria-label": "Filter settings" },
              sx: { minWidth: 220 }
            }
          ),
          /* @__PURE__ */ jsx9(
            FormControlLabel,
            {
              control: /* @__PURE__ */ jsx9(
                Switch,
                {
                  size: "small",
                  checked: hideReadOnly,
                  onChange: (event) => onHideReadOnlyChange(event.target.checked)
                }
              ),
              label: /* @__PURE__ */ jsx9(Typography6, { variant: "body2", children: "Hide non-applicable settings" })
            }
          ),
          /* @__PURE__ */ jsx9(Typography6, { variant: "caption", color: "text.secondary", "data-testid": "settings-result-count", children: shownCount === totalCount ? `${totalCount} settings` : `${shownCount} of ${totalCount} settings` })
        ] }) : /* @__PURE__ */ jsx9(Box6, {}),
        showJsonToggle ? /* @__PURE__ */ jsx9(
          Box6,
          {
            component: "button",
            type: "button",
            "data-testid": "settings-json-toggle",
            "aria-pressed": jsonOpen,
            onClick: () => onJsonOpenChange(!jsonOpen),
            sx: {
              border: 1,
              borderColor: "divider",
              borderRadius: "4px",
              backgroundColor: jsonOpen ? "action.selected" : "transparent",
              cursor: "pointer",
              padding: "4px 10px",
              fontSize: 12,
              color: "text.primary"
            },
            children: jsonOpen ? "UI view" : "JSON view"
          }
        ) : null
      ]
    }
  );
}

// src/SettingsWorkbench/ui-state.ts
var DEFAULTS = {
  searchTerm: "",
  hideReadOnly: false,
  collapsedCategories: [],
  selectedKey: null,
  activeCategory: null,
  jsonOpen: false,
  jsonTab: 0
};
function initialUiState(overrides) {
  return overrides ? { ...DEFAULTS, ...overrides } : { ...DEFAULTS };
}
var sameList = (a, b) => a.length === b.length && a.every((item, index) => item === b[index]);
function workbenchUiReducer(state, action) {
  switch (action.type) {
    case "search":
      return action.term === state.searchTerm ? state : { ...state, searchTerm: action.term };
    case "hideReadOnly":
      return action.value === state.hideReadOnly ? state : { ...state, hideReadOnly: action.value };
    case "toggleCategory": {
      const collapsed = state.collapsedCategories.includes(action.category) ? state.collapsedCategories.filter((category) => category !== action.category) : [...state.collapsedCategories, action.category];
      return { ...state, collapsedCategories: collapsed };
    }
    case "setCategoriesCollapsed":
      return sameList(state.collapsedCategories, action.categories) ? state : { ...state, collapsedCategories: [...action.categories] };
    case "selectSetting":
      return state.selectedKey === action.key && state.activeCategory === action.category ? state : { ...state, selectedKey: action.key, activeCategory: action.category };
    case "selectCategory":
      return state.activeCategory === action.category && state.selectedKey === null ? state : { ...state, activeCategory: action.category, selectedKey: null };
    case "json":
      return action.open === state.jsonOpen ? state : { ...state, jsonOpen: action.open };
    case "jsonTab":
      return action.index === state.jsonTab ? state : { ...state, jsonTab: action.index };
    default:
      return state;
  }
}
function matchesSearch(setting, term) {
  const needle = term.trim().toLowerCase();
  if (needle === "") return true;
  return setting.key.toLowerCase().includes(needle) || setting.label.toLowerCase().includes(needle) || setting.description.toLowerCase().includes(needle) || setting.category.toLowerCase().includes(needle);
}
var isInert = (setting) => setting.access === "readonly" || setting.deprecated;
function filterSettings(settings, filter) {
  const needle = filter.searchTerm.trim();
  if (needle === "" && !filter.hideReadOnly) return settings;
  return settings.filter(
    (setting) => (!filter.hideReadOnly || !isInert(setting)) && matchesSearch(setting, filter.searchTerm)
  );
}
function tocSections(settings) {
  const sections = [];
  const byCategory = /* @__PURE__ */ new Map();
  for (const setting of settings) {
    let section = byCategory.get(setting.category);
    if (!section) {
      section = { category: setting.category, settings: [], allReadOnly: true };
      byCategory.set(setting.category, section);
      sections.push(section);
    }
    section.settings.push(setting);
    if (!isInert(setting)) section.allReadOnly = false;
  }
  return sections;
}

// src/SettingsWorkbench/SettingsWorkbench.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
function scrollToAnchor(id, block) {
  const element = typeof document === "undefined" ? null : document.getElementById(id);
  element?.scrollIntoView?.({ behavior: "smooth", block });
}
function SettingsWorkbench({
  settings,
  keys,
  chrome,
  slots,
  inheritanceChain,
  onSelectEntity,
  initialUiState: seed,
  pendingChanges,
  onChange,
  onSave,
  onClear,
  hostServices,
  enumOptionsByKey,
  savingKeys,
  definitionsByKey,
  searchFiltersContent = true,
  groupByCategory = true,
  readOnlyPresentation = "disabled-editor",
  emptyState = null,
  tocWidth = 220
}) {
  const showToc = chrome?.toc ?? true;
  const showBreadcrumb = chrome?.breadcrumb ?? true;
  const showSearch = chrome?.search ?? true;
  const showJsonView = chrome?.jsonView ?? true;
  const [ui, dispatch] = useReducer(workbenchUiReducer, seed, initialUiState);
  const all = useMemo4(() => selectRows(settings, keys), [settings, keys]);
  const indexRows = useMemo4(
    () => showSearch ? filterSettings(all, { searchTerm: ui.searchTerm, hideReadOnly: ui.hideReadOnly }) : all,
    [all, showSearch, ui.searchTerm, ui.hideReadOnly]
  );
  const rows = useMemo4(() => {
    if (!showSearch) return all;
    if (searchFiltersContent) return indexRows;
    return filterSettings(all, { searchTerm: "", hideReadOnly: ui.hideReadOnly });
  }, [all, indexRows, searchFiltersContent, showSearch, ui.hideReadOnly]);
  const jsonOpen = showJsonView && ui.jsonOpen;
  const { pending, changedKeys, handleChange, handleSave, handleClear } = usePendingChanges(rows, {
    pendingChanges,
    onChange,
    onSave,
    onClear
  });
  const sections = useMemo4(() => showToc ? tocSections(indexRows) : [], [showToc, indexRows]);
  const selectSetting = useCallback2((key, category) => {
    dispatch({ type: "selectSetting", key, category });
    scrollToAnchor(`setting-${key}`, "center");
  }, []);
  const selectCategory = useCallback2((category) => {
    dispatch({ type: "selectCategory", category });
    scrollToAnchor(`section-${category}`, "start");
  }, []);
  const content = jsonOpen ? /* @__PURE__ */ jsx10(
    SettingsJsonView,
    {
      settings: rows,
      inheritanceChain,
      tab: ui.jsonTab,
      onTabChange: (index) => dispatch({ type: "jsonTab", index })
    }
  ) : /* @__PURE__ */ jsx10(
    SettingsRows,
    {
      rows,
      pending,
      changedKeys,
      onChange: handleChange,
      onSave: handleSave,
      onClear: handleClear,
      readOnlyPresentation,
      hostServices,
      enumOptionsByKey,
      savingKeys,
      definitionsByKey,
      groupByCategory,
      emptyState: showSearch ? emptyState ?? /* @__PURE__ */ jsx10(DefaultEmptyState, {}) : emptyState
    }
  );
  const showHeader = showBreadcrumb && (inheritanceChain?.length ?? 0) > 0 || Boolean(slots?.headerActions);
  const showToolbar = showSearch || showJsonView;
  const showTree = Boolean(slots?.tree);
  if (!showHeader && !showToolbar && !showToc && !showTree) return content;
  return /* @__PURE__ */ jsxs8(Box7, { sx: { display: "flex", flexDirection: "column", minHeight: 0, width: "100%" }, children: [
    showHeader ? /* @__PURE__ */ jsx10(
      SettingsWorkbenchHeader,
      {
        chain: inheritanceChain,
        showBreadcrumb,
        onSelectEntity,
        headerActions: slots?.headerActions
      }
    ) : null,
    showToolbar ? /* @__PURE__ */ jsx10(
      SettingsWorkbenchToolbar,
      {
        showSearch,
        showJsonToggle: showJsonView,
        searchTerm: ui.searchTerm,
        onSearchChange: (term) => dispatch({ type: "search", term }),
        hideReadOnly: ui.hideReadOnly,
        onHideReadOnlyChange: (value) => dispatch({ type: "hideReadOnly", value }),
        jsonOpen,
        onJsonOpenChange: (open) => dispatch({ type: "json", open }),
        shownCount: indexRows.length,
        totalCount: all.length
      }
    ) : null,
    /* @__PURE__ */ jsxs8(Box7, { sx: { display: "flex", gap: "16px", minHeight: 0, flex: 1 }, children: [
      showTree ? /* @__PURE__ */ jsx10(Box7, { sx: { flexShrink: 0, overflow: "auto" }, children: slots?.tree }) : null,
      showToc ? /* @__PURE__ */ jsx10(
        SettingsWorkbenchToc,
        {
          sections,
          collapsedCategories: ui.collapsedCategories,
          selectedKey: ui.selectedKey,
          onToggleCategory: (category) => dispatch({ type: "toggleCategory", category }),
          onSelectCategory: selectCategory,
          onSelectSetting: selectSetting,
          width: tocWidth
        }
      ) : null,
      /* @__PURE__ */ jsx10(Box7, { sx: { flex: 1, minWidth: 0, overflowY: "auto", paddingBottom: "24px" }, children: content })
    ] })
  ] });
}
function DefaultEmptyState() {
  return /* @__PURE__ */ jsx10(Box7, { "data-testid": "settings-empty", sx: { padding: "24px 0", color: "text.secondary", fontSize: 14 }, children: "No settings match the current filter." });
}
export {
  DefinitionEditor,
  DefinitionHeader,
  SettingValueDisplay,
  SettingsPanel,
  SettingsWorkbench,
  filterSettings,
  hasPendingChange,
  settingChanges,
  tocSections
};
//# sourceMappingURL=index.mjs.map